import { useState, useEffect } from 'react';
import Text, {
  TEXT_COLOR,
  TEXT_SIZE,
  TEXT_KIND,
} from '@/bit/components/components.text';
import { sendTaggingInfo } from '@/utils/common';
import ICON_ID from '@gcloud-npm/utils.enums.icon-id';
import IconGlyph from '@/bit/components/components.icon-glyph';
import styles from './styles';
import Select from './select';

export const LABEL_SIZE = TEXT_SIZE;
export const LABEL_COLOR = TEXT_COLOR;
export const COMPONENT_NAME = 'radio.button-form';

function RadioButton({
  className,
  name,
  id,
  onChange,
  error,
  options,
  optionSelected,
  withCheck = false,
  withBox = true,
  column = false,
  defaultValue = '',
  label = '',
  labelSize = TEXT_SIZE.CAPTION2,
  labelColor = TEXT_COLOR.GREY6,
}) {
  const taggingInfo = {
    componentVariation: 'all',
    componentProduct: '',
    componentName: 'radio-button',
    componentContent: label,
  };
  const [inputValue, setInputValue] = useState(defaultValue);
  const handleOnchange = (event, selectOptionSelected) => {
    sendTaggingInfo(taggingInfo, 'select-option');
    setInputValue((prev) => ({
      ...prev,
      value: event?.target?.value,
      selectValue: selectOptionSelected || null,
    }));
    onChange({
      value: event?.target?.value,
      selectValue: selectOptionSelected || null,
    });
  };
  const handleOnSelectChange = (selectOptionSelected) => {
    setInputValue((prev) => ({
      value: prev?.value,
      selectValue: selectOptionSelected,
    }));
    onChange({
      value: inputValue?.value,
      selectValue: selectOptionSelected,
    });
  };
  const boxClassName = withBox ? 'radio-label--box' : '';
  const directionClassName = column ? 'radio-group--column' : '';
  const normalizeSelectOptions = (selectOptions) => {
    if (selectOptions) {
      return selectOptions.map((line) => {
        return {
          label: line.mobileNumber,
          value: line.mobileNumber,
          isChecked: line.isChecked,
        };
      });
    }
    return null;
  };
  const normalizeRadioButtonText = (item) => {
    let normalizedText;
    if (item.options?.length > 1) {
      const optionChecked = item?.options?.find((option) => option.isChecked);
      normalizedText = optionChecked?.mobileNumber || item.label?.text;
    } else if (item.options?.length === 1) {
      normalizedText = item.options[0].mobileNumber;
    } else {
      normalizedText = item.label.text;
    }
    return normalizedText;
  };

  useEffect(() => {
    setInputValue(optionSelected);
  }, [optionSelected]);
  return (
    <div className="radio">
      <div className={`radio-group ${className} ${directionClassName}`}>
        {options.map((item) => {
          const selectOptions = normalizeSelectOptions(item.options);
          let optionChecked = null;
          if (Array.isArray(selectOptions) && selectOptions.length > 1) {
            optionChecked = selectOptions?.find((option) => option.isChecked);
          } else if (
            Array.isArray(selectOptions) &&
            selectOptions.length === 1
          ) {
            [optionChecked] = selectOptions;
          }
          return (
            <div key={item.value} className="radio-item">
              <input
                className="radio-input"
                data-cy={`${item.value}__radio`}
                id={`${item.value}__${name}`}
                name={name}
                value={`${item.value}`}
                type="radio"
                aria-label={item.label?.text}
                onChange={(event) => {
                  handleOnchange(
                    event,
                    optionChecked ? { ...optionChecked } : null
                  );
                }}
                checked={`${item.value}` === inputValue?.value}
              />
              {(!item?.options ||
                (Array.isArray(item.options) && item.options.length > 0)) && (
                <label
                  htmlFor={`${item.value}__${name}`}
                  className={`radio-label ${boxClassName} ${
                    Array.isArray(item.options) &&
                    item.options.length > 0 &&
                    item.value === inputValue?.value
                      ? 'radio-label--hidden'
                      : ''
                  }`}
                >
                  <div className="radio-label__text">
                    {withCheck && <span className="radio-inner-circle" />}
                    {item.label?.icon && item.label.icon}
                    {item.label?.text && (
                      <Text
                        className={
                          item.label?.icon ? ' radio-label__text--icon' : ''
                        }
                        text={normalizeRadioButtonText(item)}
                        color={
                          item.value === inputValue?.value && withBox
                            ? TEXT_COLOR.BLUEA
                            : labelColor
                        }
                        kind={TEXT_KIND.SPAN}
                        size={labelSize}
                      />
                    )}
                    {Array.isArray(item.options) && item.options.length > 1 && (
                      <IconGlyph
                        className="radio-label__text--cheuron"
                        id={ICON_ID.CheuronDown}
                        layoutSize={{ width: 14 }}
                        color={labelColor}
                      />
                    )}
                  </div>
                </label>
              )}
              {Array.isArray(item.options) &&
                item.options.length > 0 &&
                `${item.value}` === inputValue?.value && (
                  <Select
                    className="radio"
                    name="radio-label__select"
                    instanceId="selectId"
                    options={selectOptions}
                    focused
                    value={inputValue?.selectValue || optionChecked}
                    defaultValue={inputValue?.selectValue || optionChecked}
                    isSearchable={false}
                    onChange={handleOnSelectChange}
                    openMenuOnFocus={item.options.length > 1}
                    showDropDown={item.options.length > 1}
                  />
                )}
            </div>
          );
        })}
      </div>
      {error && (
        <Text
          className="input__error"
          text={error}
          color={TEXT_COLOR.RED}
          kind={TEXT_KIND.SPAN}
          size={TEXT_SIZE.CAPTION3}
          cyData={`${id}__error`}
        />
      )}
      <style jsx>{styles}</style>
    </div>
  );
}

export default RadioButton;
